import * as unit from "@/utils/unit";
import { FormAction } from "@/utils/form-access";

export interface SidebarMenuItem {
  icon: string;
  title: string;
  vertical?: string;
  link?: string | null;
  subs?: SidebarMenuItem[];
  formAccess?: { code: string; action?: FormAction };
  privilege?: { domain: string; action?: string };
  privilegeAny?: { domain: string; action?: string }[];
  privilegePrefix?: string;
  superadmin?: boolean;
  query?: Record<string, string>;
  badge?: string;
  badgeBlink?: boolean;
}

/** Poliklinik / Rawat Jalan: `kode < '30'` (kecuali '01') + `['37','38','39']`. */
const POLIKLINIK_BASE: SidebarMenuItem[] = [
  {
    icon: "mdi-account-group",
    title: "Daftar Pasien",
    vertical: "Daftar Pasien",
    link: "dashboard-poliklinik"
  }
];

/** Klinik intensif (ICU/HCU-sore, dll) yang juga handle pasien ranap. */
const POLIKLINIK_RANAP_SIBLING: SidebarMenuItem = {
  icon: "mdi-format-list-bulleted",
  title: "Pasien Ranap",
  vertical: "Pasien Ranap",
  link: "list-ranap"
};

const POLIKLINIK_COMMON_TAIL: SidebarMenuItem[] = [
  {
    icon: "mdi-currency-usd",
    title: "Input Billing",
    vertical: "Input Billing",
    link: "input-billing"
  },
  {
    icon: "mdi-comment-alert-outline",
    title: "Tools/Setting",
    vertical: "Tools/Setting",
    link: "tools"
  }
];

/**
 * Kartu sub-laporan RP 1 — dirender halaman hub `laporan-rp1-menu`.
 * `report` = nilai `activeReport` pada `LaporanRl1RanapPage` (dikirim lewat query).
 */
export const LAPORAN_RP1_CARDS = [
  {
    report: "rekap-harian",
    title: "Rekap Harian Pasien Ranap",
    subtitle: "Sensus harian per bangsal",
    desc:
      "Pasien awal/masuk/pindahan/keluar/mati + sisa per kelas, per bangsal per tanggal. Bisa dikoreksi manual (overlay) lalu dicetak.",
    icon: "mdi-calendar-check",
    color: "indigo"
  },
  {
    report: "jumlah-pasien",
    title: "Jumlah Pasien (L/P)",
    subtitle: "Dipisah jenis kelamin",
    desc:
      "Rekap harian masuk / pindahan / keluar / dipindah / meninggal <48 jam & ≥48 jam, dipisah laki-laki dan perempuan.",
    icon: "mdi-gender-male-female",
    color: "blue"
  },
  {
    report: "data-kematian",
    title: "Data Kematian (Pasien Meninggal)",
    subtitle: "Kematian <48 jam & ≥48 jam",
    desc:
      "Daftar pasien meninggal beserta identitas, ruang, lama dirawat, penanda <48 jam / ≥48 jam, diagnosa primer-sekunder, dan jenis bayar.",
    icon: "mdi-grave-stone",
    color: "blue-grey"
  },
  {
    report: "jenis-bayar",
    title: "Jenis Bayar",
    subtitle: "Cara bayar pasien ranap",
    desc:
      "Matriks harian jenis bayar (pasien awal / masuk / keluar) dirinci menurut jenis kelamin dan kelas perawatan.",
    icon: "mdi-cash-multiple",
    color: "teal"
  },
  {
    report: "data-penderita",
    title: "Data Penderita Rawat Inap",
    subtitle: "Rekap bulanan per bangsal, kelas & cara bayar",
    desc:
      "Pasien dirawat (awal + masuk) per bangsal L/P dan per cara bayar, rinci per bangsal menurut cara bayar dan kelas (awal/masuk/dirawat), jumlah hari rawat serta BOR Bayi vs Anak & Dewasa. Bisa dicetak PDF atau diunduh Excel.",
    icon: "mdi-account-group",
    color: "green"
  },
  {
    report: "data-penderita-meninggal",
    title: "Data Penderita Dirawat & Meninggal",
    subtitle: "Kematian <48 jam & ≥48 jam per bangsal",
    desc:
      "Pasien dirawat per bangsal L/P beserta penderita meninggal <48 jam dan ≥48 jam, jumlah meninggal, serta rekap menurut jenis kelamin dan lama dirawat. Bisa dicetak PDF atau diunduh Excel.",
    icon: "mdi-heart-pulse",
    color: "red"
  },
  {
    report: "data-bor",
    title: "Data Hari Perawatan & BOR",
    subtitle: "BOR, LOS, TOI, BTO per bangsal",
    desc:
      "Jumlah pasien L/P, tempat tidur, lama dirawat, dan hari perawatan per bangsal beserta BOR, LOS, TOI, dan BTO untuk kelompok Bayi dan Anak & Dewasa. Bisa dicetak PDF atau diunduh Excel.",
    icon: "mdi-bed-outline",
    color: "orange"
  },
  {
    report: "indikator-ranap",
    route: "laporan-indikator-ranap",
    title: "Indikator Rawat Inap",
    subtitle: "Bulanan, triwulan, semester & tahunan",
    desc:
      "Pasien awal/masuk/pindahan/keluar/dipindah/meninggal L-P, hari perawatan per kelas, serta BOR, LOS, TOI, BTO, NDR, dan GDR per bangsal dengan dasar TT Real atau TT SK. Bisa dicetak PDF, diunduh Excel, atau direkap setahun.",
    icon: "mdi-chart-box-outline",
    color: "cyan"
  },
  {
    report: "barber-johnson",
    route: "laporan-barber-johnson",
    title: "Grafik Barber Johnson",
    subtitle: "Efisiensi tempat tidur",
    desc:
      "BOR, ALOS, TOI, dan BTO dipetakan pada satu bidang koordinat beserta daerah efisiensinya, lengkap dengan analisis posisi rumah sakit dan tiap bangsal.",
    icon: "mdi-chart-scatter-plot",
    color: "deep-purple"
  }
];

export const LAPORAN_IGD_CARDS = [
  {
    route: "laporan-kegiatan-igd",
    title: "Data Kegiatan IGD",
    subtitle: "Matriks harian per disposisi",
    desc:
      "Jumlah pasien Bedah / Non Bedah / Kebidanan per tanggal menurut disposisi (Dirawat, Dirujuk, Pulang, Mati, Pindah RS). Bisa dikoreksi lalu dicetak.",
    icon: "mdi-calendar-month-outline",
    color: "red",
    privilege: { domain: "laporan_kegiatan_igd" }
  },
  {
    route: "laporan-kegiatan-klinik-igd",
    title: "Kegiatan Klinik IGD",
    subtitle: "Rekap bulanan dipisah L/P",
    desc:
      "Total pasien Baru/Lama dan tindak lanjut pelayanan (Dirawat, Dirujuk, Pulang, Pindah RS, DOA, Meninggal di IGD) per jenis pelayanan, dipisah laki-laki dan perempuan.",
    icon: "mdi-clipboard-pulse-outline",
    color: "deep-orange",
    privilege: { domain: "laporan_kegiatan_klinik_igd" }
  },
  {
    route: "laporan-register-igd",
    title: "Register Pasien IGD",
    subtitle: "Buku register harian per pasien",
    desc:
      "Daftar kunjungan IGD per hari seperti buku register: asal rujukan, pengantar, penyebab, kategori, tindak lanjut, dan pembayaran. Kolom yang belum tercatat di RME dilengkapi di sini lalu dicetak.",
    icon: "mdi-book-account-outline",
    color: "indigo",
    privilege: { domain: "laporan_register_igd" }
  },
  {
    route: "laporan-los-igd",
    title: "Rekap LOS IGD",
    subtitle: "Indikator mutu per kategori triase",
    desc:
      "Persentase pasien yang keluar dari IGD sesuai target LOS per kategori triase (Merah ≤ 8 jam, Kuning ≤ 6 jam, Hijau ≤ 2 jam), lengkap dengan rata-rata, median, dan daftar pasien yang melebihi target. Pasien Hitam (DOA) tidak dihitung.",
    icon: "mdi-timer-sand",
    color: "teal",
    privilege: { domain: "laporan_los_igd" }
  },
  {
    route: "laporan-rekap-bulanan-igd",
    title: "Rekapitulasi Bulanan IGD",
    subtitle: "Rujukan, cara bayar, & rujuk tempat penuh",
    desc:
      "Rekap bulanan Rujukan Ke Atas, Rujukan dari Bawah, Pasien Datang Sendiri, Cara Pembayaran (Ralan/Ranap), dan Rujuk Tempat Penuh. Ditarik otomatis dari data RME, dapat dikoreksi manual sebelum dicetak.",
    icon: "mdi-file-chart-outline",
    color: "brown",
    privilege: { domain: "laporan_rekap_bulanan_igd" }
  }
];

export const LAPORAN_KEBIDANAN_CARDS = [
  {
    key: "register-obstetri",
    route: "laporan-register-obstetri",
    title: "Register Obstetri",
    subtitle: "Buku register pasien obstetri",
    desc:
      "Daftar pasien obstetri di Ruang Bersalin (Utari), Ruang Pergiwati, dan IGD: identitas, paritas, perdarahan, anemia, PEB, cara persalinan, data bayi, hingga KB pascasalin. Disusun otomatis dari data RME.",
    icon: "mdi-human-pregnant",
    color: "pink",
    privilege: { domain: "laporan_register_obstetri" },
    alasan: null
  },
  {
    key: "register-ginekologi",
    route: "laporan-register-ginekologi",
    title: "Register Ginekologi",
    subtitle: "Buku register pasien ginekologi",
    desc:
      "Daftar pasien ginekologi di Ruang Bersalin (Utari), Ruang Pergiwati, dan IGD: diagnosa masuk & keluar, tindakan, penyebab kematian, mati kurang/lebih 24 jam, dan KB. Disusun otomatis dari data RME.",
    icon: "mdi-gender-female",
    color: "purple",
    privilege: { domain: "laporan_register_ginekologi" },
    alasan: null
  },
  {
    key: "rekap-obstetri",
    route: "laporan-rekap-obstetri",
    title: "Rekapan Obstetri",
    subtitle: "Rekap bulanan kasus obstetri",
    desc:
      "Jumlah kasus obstetri, indikasi SC, persalinan pervaginam, rujukan, IMD, bumil risti, serta abortus & KET per bulan, dihitung dari data RME; baris tanpa sumber RME dan koreksi angka diisi PIC.",
    icon: "mdi-table-large",
    color: "deep-purple",
    privilege: { domain: "laporan_rekap_obstetri" },
    alasan: null
  },
  {
    key: "rl36",
    route: "laporan-rl36-kebidanan",
    title: "RL 3.6 Kebidanan",
    subtitle: "Formulir RL 3.6 per bulan",
    desc:
      "Rekapitulasi kegiatan pelayanan kebidanan Januari–Desember dalam format formulir rumah sakit: ANC, persalinan, komplikasi obstetri & non obstetri, aborsi, nifas. Dihitung dari data RME; baris tanpa sumber RME dan koreksi angka diisi PIC.",
    icon: "mdi-file-table-outline",
    color: "indigo",
    privilege: { domain: "laporan_rl36_kebidanan" },
    alasan: null
  }
];

export const LAPORAN_BANK_DARAH_CARDS = [
  {
    key: "reaksi-transfusi",
    route: "laporan-reaksi-transfusi",
    title: "Laporan Kejadian Reaksi Transfusi",
    subtitle: "Dari Monitoring Transfusi Darah",
    desc:
      "Rekap kejadian reaksi transfusi per periode, ruang, dan jenis reaksi; tiap kejadian dapat dicetak sesuai blanko laporan.",
    icon: "mdi-water-alert",
    color: "red",
    alasan: null
  },
  {
    key: "rekap-order",
    route: "laporan-rekap-order-bank-darah",
    title: "Rekap Order Bank Darah",
    subtitle: "Dari order bangsal/poli ke Bank Darah",
    desc:
      "Rekap order permintaan darah per periode, jenis, unit pemohon, dan status (order masuk, diterima, menunggu sampel, diproses, selesai, dibatalkan).",
    icon: "mdi-clipboard-list-outline",
    color: "indigo",
    alasan: null
  },
  {
    key: "rekap-transaksi",
    route: "laporan-rekap-transaksi-bank-darah",
    title: "Rekap Transaksi Bank Darah",
    subtitle: "Dari SIM Bank Darah",
    desc:
      "Rekap transaksi kantong darah & pemeriksaan per periode, pelayanan, jenis rawat, unit, dan cara bayar, berikut rincian per transaksi.",
    icon: "mdi-swap-horizontal",
    color: "blue",
    alasan: null
  },
  {
    key: "rekap-serah-terima",
    route: "laporan-rekap-serah-terima-darah",
    title: "Rekap Serah Terima Komponen & Sampel Darah",
    subtitle: "Dari Serah Terima Komponen dan Sampel",
    desc:
      "Rekap serah terima per periode diterima, unit pemohon, jenis order, status serah terima (diterima/draft), serta petugas penerima & pengantar.",
    icon: "mdi-handshake-outline",
    color: "teal",
    alasan: null
  },
  {
    key: "rekap-serah-terima-kantong",
    route: "laporan-rekap-serah-terima-kantong-darah",
    title: "Rekap Serah Terima Kantong Darah",
    subtitle: "Dari Serah Terima Kantong Darah BDRS",
    desc:
      "Rekap kantong darah yang diserahkan ke bangsal per periode diambil: komponen, golongan darah, nomor kantong, serta petugas bangsal & BDRS.",
    icon: "mdi-blood-bag",
    color: "deep-orange",
    alasan: null
  }
];

export const BANK_DARAH_SERAH_TERIMA_GROUP: SidebarMenuItem = {
  icon: "mdi-handshake-outline",
  title: "Serah Terima dan Pembatalan",
  vertical: "Serah Terima dan Pembatalan Bank Darah",
  link: null,
  subs: [
    {
      icon: "mdi-test-tube",
      title: "Serah Terima Komponen dan Sampel",
      vertical: "Serah Terima Komponen dan Sampel",
      link: "bank-darah-serah-terima-sampel",
      privilege: { domain: "bank_darah_order" }
    },
    {
      icon: "mdi-blood-bag",
      title: "Serah Terima Kantong Darah",
      vertical: "Serah Terima Kantong Darah",
      link: "bank-darah-serah-terima-kantong",
      privilege: { domain: "bank_darah_order" }
    },
    {
      icon: "mdi-water-remove",
      title: "Pembatalan Komponen Darah",
      vertical: "Pembatalan Komponen Darah",
      link: "bank-darah-pembatalan-komponen",
      privilege: { domain: "bank_darah_order" }
    }
  ]
};

const LAPORAN_BANK_DARAH_ITEM: SidebarMenuItem = {
  icon: "mdi-water-alert",
  title: "Laporan Bank Darah",
  vertical: "Laporan Bank Darah — Reaksi Transfusi & rekap Bank Darah",
  link: "laporan-bank-darah-menu",
  privilege: { domain: "laporan_bank_darah" }
};

export const LAPORAN_GROUP: SidebarMenuItem = {
  icon: "mdi-note",
  title: "Laporan",
  vertical: "Laporan Rumah Sakit",
  link: null,
  subs: [
    {
      icon: "mdi-bed",
      title: "Laporan RP 1 Ranap",
      vertical:
        "RP 1 — Rekapitulasi Harian Pasien Rawat Inap (Permenkes 1171/2011)",
      link: "laporan-rp1-menu"
    },
    {
      icon: "mdi-database-export-outline",
      title: "Laporan SIRS 6.3",
      vertical: "SIRS Revisi 6.3 — RL 1 s.d. RL 5 (Kemenkes)",
      link: "laporan-sirs"
    },
    {
      icon: "mdi-clipboard-check-multiple-outline",
      title: "Rekap Persetujuan Pasien",
      vertical:
        "Rekap persetujuan & penolakan pasien per form, unit, dokter, dan periode",
      link: "laporan-rekap-persetujuan-pasien",
      privilege: { domain: "laporan_persetujuan_pasien" }
    },
    {
      icon: "mdi-hospital-box-outline",
      title: "Laporan IGD",
      vertical:
        "Data Kegiatan IGD, Kegiatan Klinik IGD, Register Pasien IGD & Rekap LOS IGD",
      link: "laporan-igd-menu",
      privilegeAny: [
        { domain: "laporan_kegiatan_igd" },
        { domain: "laporan_kegiatan_klinik_igd" },
        { domain: "laporan_register_igd" },
        { domain: "laporan_los_igd" }
      ]
    },
    {
      icon: "mdi-mother-nurse",
      title: "Laporan Kebidanan/IMP",
      vertical:
        "Register Obstetri, Register Ginekologi, Rekapan Obstetri & RL 3.6 Kebidanan",
      link: "laporan-kebidanan-menu",
      privilegeAny: [
        { domain: "laporan_register_obstetri" },
        { domain: "laporan_register_ginekologi" },
        { domain: "laporan_rekap_obstetri" },
        { domain: "laporan_rl36_kebidanan" }
      ]
    },
    {
      icon: "mdi-clipboard-text-outline",
      title: "Register Harian Rawat Jalan",
      vertical: "Cetak Register Harian Rawat Jalan (per poliklinik)",
      link: "register-ralan-harian"
    },
    LAPORAN_BANK_DARAH_ITEM,
    {
      icon: "mdi-bacteria",
      title: "Laporan PPRA",
      vertical:
        "Diagnosa & antibiotik yang diberikan selama perawatan (AWaRe WHO)",
      link: "laporan-ppra",
      privilege: { domain: "laporan_ppra" }
    },
    {
      icon: "mdi-download-outline",
      title: "Unduhan Lainnya",
      vertical: "Unduhan Register HF & Skrining Batuk",
      link: "report"
    }
  ]
};

/** IGD: `unit_id == '01'`. */
const IGD_MENU: SidebarMenuItem[] = [
  {
    icon: "mdi-account-group",
    title: "Daftar Pasien",
    vertical: "Daftar Pasien",
    link: "daftar-pasien-igd"
  },
  {
    icon: "mdi-currency-usd",
    title: "Input Billing",
    vertical: "Input Billing",
    link: "input-billing"
  },
  {
    icon: "mdi-comment-alert-outline",
    title: "Lapor/Feedback",
    vertical: "Lapor SIMRS dan Feedback",
    link: "feedback"
  }
];

/** Ranap: `'50' <= kode < '80'` atau `'33' <= kode < '37'`. */
const RANAP_MENU: SidebarMenuItem[] = [
  {
    icon: "mdi-account-group",
    title: "Daftar Pasien",
    vertical: "Daftar Pasien",
    link: "daftar-pasien"
  },
  {
    icon: "mdi-human",
    title: "Asuhan Keperawatan",
    vertical: "Asuhan Keperawatan",
    link: "askep-ranap",
    privilege: { domain: "perencanaan-askep", action: "view" }
  },
  {
    icon: "mdi-currency-usd",
    title: "Input Billing",
    vertical: "Input Billing",
    link: "input-billing"
  },
  {
    icon: "mdi-comment-alert-outline",
    title: "Lapor/Feedback",
    vertical: "Lapor SIMRS dan Feedback",
    link: "feedback"
  }
];

/** Penunjang medis (Lab/Radiologi): `'31' <= kode <= '32'`. */
const PENUNJANG_MENU: SidebarMenuItem[] = [
  {
    icon: "mdi-account-group",
    title: "Daftar Pasien",
    vertical: "Daftar Pasien",
    link: "daftar-pasien-unit"
  },
  {
    icon: "mdi-comment-alert-outline",
    title: "Lapor/Feedback",
    vertical: "Lapor SIMRS dan Feedback",
    link: "feedback"
  }
];

/** Rekam Medis (`'80'`) / Pendaftaran (`'81'`). */
const PENDAFTARAN_MENU: SidebarMenuItem[] = [
  {
    icon: "mdi-account-group",
    title: "Daftar Pasien",
    vertical: "Daftar Pasien",
    link: "daftar-pasien-pendaftaran"
  },
  {
    icon: "mdi-comment-alert-outline",
    title: "Lapor/Feedback",
    vertical: "Lapor SIMRS dan Feedback",
    link: "feedback"
  }
];

const ADMINISTRASI_MENU: SidebarMenuItem[] = [
  {
    icon: "mdi-account-group",
    title: "Daftar Pasien",
    vertical: "Daftar Pasien",
    link: "daftar-pasien-administrasi"
  },
  {
    icon: "mdi-currency-usd",
    title: "Input Billing",
    vertical: "Input Billing",
    link: "input-billing"
  },
  {
    icon: "mdi-bed",
    title: "Laporan RP 1",
    vertical:
      "RP 1 — Rekapitulasi Harian Pasien Rawat Inap (Permenkes 1171/2011)",
    link: "laporan-rp1-menu"
  },
  {
    icon: "mdi-folder-multiple-image",
    title: "Upload Berkas RM",
    vertical:
      "Unggah & kelola berkas manual RM pasien (scan RM lama, KTP, KK, BPJS)",
    link: "berkas-rm"
  },
  {
    icon: "mdi-comment-alert-outline",
    title: "Lapor/Feedback",
    vertical: "Lapor SIMRS dan Feedback",
    link: "feedback"
  }
];

/** Default — admin/manajemen/unit lain. */
const ADMIN_MENU: SidebarMenuItem[] = [
  {
    icon: "mdi-home",
    title: "Home",
    vertical: "Home",
    link: "dashboard-lain"
  },
  {
    icon: "mdi-comment-alert-outline",
    title: "Lapor/Feedback",
    vertical: "Lapor SIMRS dan Feedback",
    link: "feedback"
  }
];

export const DASHBOARD_DOKTER_MENU_ITEM: SidebarMenuItem = {
  icon: "mdi-view-dashboard",
  title: "Dashboard",
  vertical: "Dashboard Dokter",
  link: "dashboard-dokter"
};

export function resolvePrimaryMenu(unitId: string): SidebarMenuItem[] {
  const k = (unitId || "").trim();

  // Poliklinik (>= "02" && < "30") || ['37','38','39']
  if ((k >= "02" && k < "30") || ["37", "38", "39", "3A"].includes(k)) {
    const out = [...POLIKLINIK_BASE];
    // Klinik intensif-sore yang juga handle pasien ranap
    if (["27", "2L", "38"].includes(k)) {
      out.push(POLIKLINIK_RANAP_SIBLING);
    }
    out.push(...POLIKLINIK_COMMON_TAIL);
    return out;
  }

  if (unit.isGadar(k)) return IGD_MENU;

  // Ranap medis/bedah + kebidanan/perinatal
  if ((k >= "50" && k < "80") || (k >= "33" && k < "37")) {
    return RANAP_MENU;
  }

  // Penunjang medis (Lab/Radiologi) + Instalasi Gizi
  if (unit.isDaftarPasienUnit(k)) return PENUNJANG_MENU;

  if (unit.isRekamMedisPendaftaran(k)) return PENDAFTARAN_MENU;

  if (unit.isUnitAdministrasi(k)) return ADMINISTRASI_MENU;

  return ADMIN_MENU;
}

const SUPERVISI_GROUP: SidebarMenuItem = {
  icon: "mdi-clipboard-check-outline",
  title: "Supervisi",
  vertical: "Supervisi",
  link: null,
  subs: [
    {
      icon: "mdi-ambulance",
      title: "Ambulan",
      vertical: "Supervisi Ambulan",
      link: "supervisi-ambulans-list",
      privilege: { domain: "supervisi_ambulan" }
    },
    {
      icon: "mdi-spray-bottle",
      title: "CSSD",
      vertical: "Supervisi CSSD",
      link: "supervisi-cssd-list",
      privilege: { domain: "supervisi_cssd" }
    },
    {
      icon: "mdi-clipboard-pulse-outline",
      title: "IBS (Bedah Sentral)",
      vertical: "Supervisi PPI — Instalasi Bedah Sentral (IBS)",
      link: "supervisi-ibs-list",
      privilege: { domain: "supervisi_ibs" }
    },
    {
      icon: "mdi-pill",
      title: "Farmasi",
      vertical: "Supervisi Farmasi",
      link: "supervisi-farmasi-list",
      privilege: { domain: "supervisi_farmasi" }
    },
    {
      icon: "mdi-shield-check",
      title: "Unit Ruang",
      vertical: "Supervisi Unit Ruang (PPI)",
      link: "ppi-supervisi-umum",
      privilege: { domain: "supervisi_umum" }
    },
    {
      icon: "mdi-food-apple",
      title: "Gizi",
      vertical: "Supervisi Gizi",
      link: "gizi-supervisi",
      privilege: { domain: "supervisi_gizi" }
    },
    {
      icon: "mdi-flask",
      title: "Lab",
      vertical: "Supervisi Lab",
      link: "lab-supervisi",
      privilege: { domain: "supervisi_lab" }
    },
    {
      icon: "mdi-washing-machine",
      title: "Laundry",
      vertical: "Supervisi Unit Laundry",
      link: "supervisi-laundry",
      privilege: { domain: "supervisi_laundry" }
    },
    {
      icon: "mdi-hospital-building",
      title: "Kamar Jenazah",
      vertical: "Supervisi Kamar Jenazah",
      link: "supervisi-jenazah",
      privilege: { domain: "supervisi_jenazah" }
    },
    {
      icon: "mdi-recycle-variant",
      title: "Sanitasi",
      vertical: "Supervisi Unit Sanitasi",
      link: "supervisi-sanitasi-list",
      privilege: { domain: "supervisi_sanitasi" }
    },
    {
      icon: "mdi-blood-bag",
      title: "BDRS",
      vertical: "Supervisi Unit BDRS (Bank Darah RS)",
      link: "supervisi-bdrs-list",
      privilege: { domain: "supervisi_bdrs" }
    }
  ]
};

const AUDIT_MENU_ITEM: SidebarMenuItem = {
  icon: "mdi-clipboard-check-multiple-outline",
  title: "Audit",
  vertical: "Audit PPI & Kesling — pilih program audit",
  link: "ppi-audit-menu",
  privilegePrefix: "audit_"
};

const SURVEILANS_MENU_ITEM: SidebarMenuItem = {
  icon: "mdi-clipboard-pulse",
  title: "Surveilans",
  vertical: "Surveilans PPI (Hand Hygiene & APD) — pilih program surveilans",
  link: "ppi-surveilans-menu",
  privilegePrefix: "surveilans_"
};

const PPI_REKAP_ITEMS: SidebarMenuItem[] = [
  {
    icon: "mdi-chart-line",
    title: "Rekap Bundle PPI",
    vertical:
      "Rekap Kepatuhan Bundle HAIs (VAP/ISK/IAD/IDO) per bulan — semua bangsal & per bangsal, TTD Ketua Komite PPI",
    link: "ppi-bundle-rekap-bulanan",
    privilege: { domain: "ppi_bundle_rekap_bulanan" }
  },
  {
    icon: "mdi-recycle-variant",
    title: "Rekap Penggunaan Alat Reuse",
    vertical:
      "Rekap pemakaian alat single-use reuse per pasien — filter per bangsal/unit, periode, dan jenis alat",
    link: "ppi-rekap-alat-reuse",
    privilege: { domain: "ppi_bundle_rekap_bulanan" }
  },
  {
    icon: "mdi-chart-bar",
    title: "Rekap Surveilans & Audit",
    vertical: "Rekap Surveilans & Audit PPI per Ruangan (tabel + grafik)",
    link: "ppi-rekap",
    privilege: { domain: "ppi_rekap_ruangan" }
  }
];

export const ALAT_REUSE_GROUP: SidebarMenuItem = {
  icon: "mdi-recycle-variant",
  title: "Alat Reuse",
  vertical: "Pencatatan Penggunaan Alat Single-Use Reuse",
  link: null,
  subs: [
    {
      icon: "mdi-alert-octagon-outline",
      title: "Laporan Kerusakan",
      vertical: "Log kerusakan alat reuse",
      link: "alat-reuse-kerusakan",
      formAccess: { code: "penggunaan_alat_reuse" }
    }
  ]
};

const MUTU_GROUP: SidebarMenuItem = {
  icon: "mdi-chart-line",
  title: "Mutu",
  vertical: "Indikator Mutu",
  link: null,
  subs: [
    {
      icon: "mdi-stethoscope",
      title: "Kepatuhan Visite Dokter Spesialis",
      vertical: "Kepatuhan Visite & Scorecard DPJP",
      link: "kepatuhan-visite-dpjp",
      privilege: { domain: "mutu_visite_dokter" }
    },
    {
      icon: "mdi-alert-circle",
      title: "Pelaporan Hasil Kritis Lab",
      vertical: "Pelaporan Hasil Kritis Laboratorium",
      link: "pelaporan-hasil-kritis-lab",
      privilege: { domain: "mutu_hasil_kritis_lab" }
    },
    {
      icon: "mdi-pill",
      title: "Kepatuhan FORNAS",
      vertical: "Kepatuhan Penggunaan Formularium Nasional",
      link: "kepatuhan-fornas",
      privilege: { domain: "mutu_fornas" }
    },
    {
      icon: "mdi-clock-alert-outline",
      title: "Penundaan Operasi Elektif",
      vertical: "Penundaan Operasi Elektif (IBS)",
      link: "penundaan-operasi-elektif",
      privilege: { domain: "mutu_penundaan_operasi" }
    },
    {
      icon: "mdi-clipboard-pulse-outline",
      title: "Kepatuhan CP",
      vertical: "Kepatuhan Clinical Pathway",
      link: "cp-compliance",
      privilege: { domain: "mutu_clinical_pathway" }
    },
    {
      icon: "mdi-ambulance",
      title: "Waktu Tanggap SC",
      vertical: "Waktu Tanggap SC Emergensi",
      link: "sc-emergency",
      privilege: { domain: "mutu_waktu_tanggap_sc" }
    },
    {
      icon: "mdi-timer-sand",
      title: "Waktu Tunggu Rajal",
      vertical: "Waktu Tunggu Rawat Jalan",
      link: "outpatient-waiting",
      privilege: { domain: "mutu_waktu_tunggu_ralan" }
    },
    {
      icon: "mdi-message-alert-outline",
      title: "Kecepatan Tanggap Komplain",
      vertical: "Kecepatan Waktu Tanggap Komplain",
      link: "complaint-response",
      privilege: { domain: "mutu_tanggap_komplain" }
    }
  ]
};

const KMKP_GROUP: SidebarMenuItem = {
  icon: "mdi-shield-check-outline",
  title: "KMKP",
  vertical: "Komite Mutu & Keselamatan Pasien",
  link: null,
  subs: [
    {
      icon: "mdi-shield-account-outline",
      title: "Audit SKP 1 — Identifikasi Pasien",
      vertical: "SKP 1 — Kepatuhan Identifikasi Pasien",
      link: "kepatuhan-identifikasi-pasien",
      privilege: { domain: "mutu_identifikasi_pasien" }
    },
    {
      icon: "mdi-human-cane",
      title: "Kepatuhan Pencegahan Jatuh (SKP 6)",
      vertical: "SKP 6 — Kepatuhan Pencegahan Jatuh",
      link: "kepatuhan-jatuh",
      privilege: { domain: "mutu_pencegahan_jatuh" }
    }
  ]
};

const SUPERVISI_PPI_GROUP: SidebarMenuItem = {
  ...SUPERVISI_GROUP,
  title: "Supervisi & PPI",
  vertical: "Supervisi, Komite PPI & Kesling",
  subs: [
    ...(SUPERVISI_GROUP.subs ?? []),
    AUDIT_MENU_ITEM,
    SURVEILANS_MENU_ITEM,
    ...PPI_REKAP_ITEMS
  ]
};

const MUTU_KMKP_GROUP: SidebarMenuItem = {
  ...MUTU_GROUP,
  title: "Mutu & KMKP",
  vertical: "Indikator Mutu & Komite Mutu Keselamatan Pasien",
  subs: [...(MUTU_GROUP.subs ?? []), ...(KMKP_GROUP.subs ?? [])]
};

const URM_GROUP: SidebarMenuItem = {
  icon: "mdi-archive-outline",
  title: "URM",
  vertical: "Unit Rekam Medis & Informasi Kesehatan",
  link: null,
  subs: [
    {
      icon: "mdi-file-document-multiple-outline",
      title: "Audit Alih Media RM",
      vertical: "Audit Alih Media RM Kertas → RME (PMK 24/2022 + MRMIK)",
      link: "RetensiAlihMediaDaftar"
    },
    {
      icon: "mdi-file-search-outline",
      title: "Audit Kuantitatif RME",
      vertical: "Analisis Rekam Medis Kuantitatif (Kelengkapan Pengisian RME)",
      link: "audit-kuantitatif-rme"
    }
  ]
};

export const AUDIT_TRAIL_MENU_ITEM: SidebarMenuItem = {
  icon: "mdi-history",
  title: "Audit Trail",
  vertical: "Riwayat Mutasi Data Lintas-Entitas (Filter & Ekspor)",
  link: "superadmin-audit",
  privilege: { domain: "audit_trail" }
};

/**
 * Menu khusus Super Admin (role_id == 1).
 */
export const SUPERADMIN_GROUP: SidebarMenuItem = {
  icon: "mdi-shield-star-outline",
  title: "Super Admin",
  vertical: "Super Admin — Akses & Kontrol Penuh",
  link: null,
  subs: [
    {
      icon: "mdi-view-dashboard-variant-outline",
      title: "Dashboard",
      vertical: "Dashboard Super Admin (KPI Realtime)",
      link: "superadmin-dashboard"
    },
    {
      icon: "mdi-account-multiple-outline",
      title: "Kelola User",
      vertical: "Manajemen User, Role Sistem & Tanda Tangan",
      link: "user-list"
    },
    {
      icon: "mdi-shield-key-outline",
      title: "Hak Akses (RBAC)",
      vertical: "Profession-Role & Privilege Granular",
      link: "superadmin-rbac"
    },
    {
      icon: "mdi-cog-outline",
      title: "Pengaturan Terpusat",
      vertical: "Kapasitas TT & Penomoran Surat",
      link: "superadmin-settings"
    },
    AUDIT_TRAIL_MENU_ITEM,
    {
      icon: "mdi-file-chart-outline",
      title: "Laporan Login",
      vertical: "Login Report",
      link: "login-report"
    }
  ]
};

export const MASTER_REFERENSI_MENU_ITEM: SidebarMenuItem = {
  icon: "mdi-database-cog-outline",
  title: "Master Referensi",
  vertical:
    "Master Referensi — kategori berkas RM, alat reuse, ref askep, dokumen umum, tim penguji",
  link: "master-referensi-menu",
  privilegeAny: [
    { domain: "master_jenis_berkas" },
    { domain: "master_alat_reuse" },
    { domain: "master_ref_askep" },
    { domain: "master_general_document" },
    { domain: "master_tim_penguji" }
  ]
};

export const ANTIMIKROBA_RESERVE_MENU_ITEM: SidebarMenuItem = {
  icon: "mdi-pill",
  title: "Permintaan Antimikroba",
  vertical:
    "Permintaan Antimikroba Reserve — catat hasil konsul & verifikasi KPRA",
  link: "permintaan-antimikroba-reserve",
  privilege: { domain: "antimikroba_reserve" }
};

export const ORDER_GIZI_GROUP: SidebarMenuItem = {
  icon: "mdi-food-variant",
  title: "Order Gizi",
  vertical: "Order Gizi — pemesanan makanan pasien rawat inap",
  link: null,
  subs: [
    {
      icon: "mdi-account-group",
      title: "Daftar Pasien Order Gizi",
      vertical: "Pasien rawat inap yang masih dirawat dan ada di pesanan makanan",
      link: "order-gizi-daftar-pasien",
      formAccess: { code: "order_gizi_daftar_pasien" }
    },
    {
      icon: "mdi-silverware-fork-knife",
      title: "Pemesanan Makanan",
      vertical: "Pemesanan makanan pasien bangsal per tanggal & shift",
      link: "order-gizi-pemesanan",
      formAccess: { code: "order_gizi_pemesanan" }
    },
    {
      icon: "mdi-clipboard-list-outline",
      title: "Rekap Pemesanan",
      vertical: "Rekap pemesanan makanan — filter, cetak PDF & Excel",
      link: "order-gizi-rekap",
      formAccess: { code: "order_gizi_rekap" }
    }
  ]
};

export const MPP_GROUP: SidebarMenuItem = {
  icon: "mdi-bed-outline",
  title: "MPP",
  vertical: "Manajer Pelayanan Pasien (MPP)",
  link: null,
  subs: [
    {
      icon: "mdi-bed-outline",
      title: "Bed Management",
      vertical: "Bed Management — Format Bed Manajemen (MPP)",
      link: "bed-management-list",
      privilege: { domain: "mpp_bed_management" }
    }
  ]
};

const SETTINGS_GROUP: SidebarMenuItem = {
  icon: "mdi-cog-outline",
  title: "Settings",
  vertical: "Settings",
  link: null,
  subs: [
    {
      icon: "mdi-account-circle",
      title: "User",
      vertical: "User",
      link: "user-list"
    }
  ]
};

export const MUTU_PPI_MENU: SidebarMenuItem[] = [
  SUPERVISI_PPI_GROUP,
  MUTU_KMKP_GROUP
];

export const MANAJEMEN_MENU: SidebarMenuItem[] = [
  URM_GROUP,
  SETTINGS_GROUP
];

export interface MenuAccessContext {
  canAccess: (code: string, action?: FormAction) => boolean;
  hasPrivilege: (domain: string, action: string) => boolean;
  hasPrivilegePrefix: (prefix: string, action: string) => boolean;
  isSuperAdmin?: boolean;
}

export function filterMenuByAccess(
  items: SidebarMenuItem[],
  ctx: MenuAccessContext
): SidebarMenuItem[] {
  const isSuperAdmin = ctx.isSuperAdmin === true;

  const isVisible = (item: SidebarMenuItem): boolean => {
    if (item.superadmin && !isSuperAdmin) return false;
    if (item.privilege) {
      return ctx.hasPrivilege(
        item.privilege.domain,
        item.privilege.action ?? "view"
      );
    }
    if (item.privilegeAny) {
      return item.privilegeAny.some(p =>
        ctx.hasPrivilege(p.domain, p.action ?? "view")
      );
    }
    if (item.privilegePrefix) {
      return ctx.hasPrivilegePrefix(item.privilegePrefix, "view");
    }
    if (!item.formAccess) return true;
    return ctx.canAccess(
      item.formAccess.code,
      item.formAccess.action ?? "view"
    );
  };

  const out: SidebarMenuItem[] = [];
  for (const item of items) {
    // Sub-menu group
    if (item.subs && item.subs.length > 0) {
      if (item.superadmin && !isSuperAdmin) continue;
      const filteredSubs = item.subs.filter(isVisible);
      if (filteredSubs.length > 0) {
        out.push({ ...item, subs: filteredSubs });
      }
      continue;
    }
    // Leaf item
    if (isVisible(item)) {
      out.push(item);
    }
  }
  return out;
}
